import React, { useState, useEffect, useRef } from "react";
import { Button, Select, Table, message, Spin, Tooltip, Affix, Progress, Modal, Tag } from "antd";
import { ThunderboltOutlined, FileOutlined } from "@ant-design/icons";
import styled, { createGlobalStyle } from "styled-components";
import { GrDocumentDownload } from "react-icons/gr";
import { insertInstructionText, insertTemplateIntoWordPLPS } from "../../taskpane";
import { getPLPSTemplateList, getPLPSTemplateSection, triggePLPSPlanner } from "../../../services/plpsService";

const { Option } = Select;

/* ---------- GLOBAL STYLES ---------- */
const GlobalStyle = createGlobalStyle`
  * {
    font-family: 'Open Sans', sans-serif !important;
  }
`;

const StyledButton = styled(Button)`
  background: #e3ecff;
  color: #426bba !important;
  font-weight: 600;
  font-size: 14px;
  border: none;
  border-radius: 1rem;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  padding: 4px 14px;
  margin-bottom: 14px;

  &:hover {
    background: #d7e4ff !important;
    color: #426bba !important;
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  height: 32px;
  font-size: 14px;
  font-weight: 600;
  color: #426bba;
  border-radius: 1rem !important;
  box-shadow: 0 2px 6px rgba(66, 107, 186, 0.2);
  text-align: center;

  && .ant-select-selector {
    background-color: #e3ecff !important;
    font-size: 14px;
    font-weight: 600;
    border: none !important;
    border-radius: 1rem !important;
    padding: 4px 16px;
    color: #426bba !important;
    text-align: center;
  }

  && .ant-select-selection-item,
  && .ant-select-selection-placeholder {
    font-size: 14px;
    font-weight: 600;
    color: #426bba !important;
    text-align: center;
  }

  && .ant-select-arrow {
    color: #426bba !important;
  }

  &:hover .ant-select-selector {
    background-color: #d7e4ff !important;
  }
`;

export const StyledTableWrapper = styled.div`
  .ant-table-thead > tr > th {
    background: #426bba !important;
    border: 1px solid #426bba;
    color: #fefeff !important;
    font-weight: 600;
    font-size: 12px;
  }
  .ant-table-tbody > tr > td {
    background: linear-gradient(
      150deg,
      rgba(235, 240, 255, 1) 25%,
      rgba(225, 235, 255, 1) 50%,
      rgba(215, 230, 255, 1) 100%
    );
    border: 1px solid #bfd3f2;
    color: #2c4c91;
    font-size: 11px;
    padding: 6px 12px;
  }
`;

const StyledProgress = styled(Progress)`
  .ant-progress-bg {
    background-color: #426bba !important;
  }
  .ant-progress-text {
    font-size: 11px !important;
    color: #426bba !important;
  }
`;

const FileNameTag = ({ fileName, showIconOnly = false }) => (
  <Tooltip title={fileName}>
    <Tag
      color="blue"
      style={{
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
        cursor: "pointer",
      }}
    >
      <FileOutlined />
      {!showIconOnly && (
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {fileName}
        </span>
      )}
    </Tag>
  </Tooltip>
);

const PLPSTemplate = ({ protocolId, protocolName, synopsisText, userEmail, userId, onBack, fileList, euctNumber }) => {
  const [loading, setLoading] = useState(false);
  const [rowLoader, setRowLoader] = useState(false);
  const [rowLoading, setRowLoading] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [sections, setSections] = useState([]);
  const [history, setHistory] = useState({});
  const [templateLoad, setTemplateLoad] = useState(false);
  const [generateAllLoading, setGenerateAllLoading] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [selectedRunIndexes, setSelectedRunIndexes] = useState({});
  const [instructionText, setInstructionText] = useState("");

  const abortControllerRef = useRef(null);
  const cancelGenerationRef = useRef(false);

  const abortControllerHandle = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // cancels the request
    }
  };

  const fetchConfiguredDataset = async (template_id) => {
    try {
      setLoading(true);
      const dataset = await getPLPSTemplateSection({ template_id });
      // console.log("Configured Dataset", dataset);
      const sections = dataset?.response || []
      const finalSections = sections.length > 8 ? sections.slice(0, sections.length - 2) : sections;
      setSections(finalSections);
    } catch {
      message.error("Failed to load template dataset");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setTemplateLoad(true);
      try {
        const res = await getPLPSTemplateList();
        // console.log("Template List", res);
        const list = res?.response || [];
        const instructionText = res?.instruction_text || "";
        // const metaDataObj = res?.data?.metadata || {};
        if (!isMounted) return;

        setTemplates(list);
        setInstructionText(instructionText)
        // setMetadata(metaDataObj);
        // setSideEffects(res?.data?.side_effects || []);
        //Auto-select if there’s exactly one template
        if (list.length === 1) {
          const onlyTemplate = list[0];
          setTemplateId(onlyTemplate.template_id);
          setLoading(true);
          message.info(`Auto selected: ${onlyTemplate.template_name}`);
          await fetchConfiguredDataset(onlyTemplate.template_id);
        }
      } catch {
        message.error("Failed to load ICF Template");
      } finally {
        if (isMounted) setTemplateLoad(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleTemplateSelect = async (val, option) => {
    if (val === templateId) return;

    setTemplateId(val);
    await fetchConfiguredDataset(val);
  };

  const buildPLPSPayload = (section) => {
    return {
      section_name: section?.section_name || "",
      prompt: section?.prompts || "",
      template: section?.template || "",
      digitized_data: synopsisText || "",
      user_id: userId,
      user_name: userEmail,
      protocol_id: protocolId,
      protocol_name: protocolName || "",
    };
  };

  const handleGenerateForSection = async (section, index = null) => {
    if (index !== null) {
      setRowLoading(index);
      setRowLoader(true);
    }
    if (cancelGenerationRef.current) {
      console.warn("Generation aborted before API call.");
      return;
    }
    abortControllerRef.current = new AbortController();

    try {
      let generatedText = "";
      let payload = "";
      //Uncomment if Sections are getting Static text or Context Lib
      // if (section?.standard_flag === "true" && section?.standard_contextlib === "false") {
      //   generatedText = section?.standard_text ?? "";
      //   console.log("Using standard_text (skipped planner API):", section?.standard_text);
      // } else if (section?.standard_flag === "true" && section?.standard_contextlib === "true") {
      //   payload = buildICFContextPayload(section);
      //   console.log("ICF Context Library Payload:", payload);
      //   const res = await getICFContextLib(payload, abortControllerRef.current.signal);
      //   console.log("Standard Context Library Api Response", res);
      //   if (Array.isArray(res)) {
      //     generatedText = `<div class="procedure-list"><ul>${res
      //       .map((item) => `<li><strong>${item["PROCEDURE NAME"]}</strong>: ${item["DESCRIPTION"]}</li>`)
      //       .join("")}</ul></div>`;
      //   } else {
      //     generatedText = "No context library data available.";
      //   }
      // } else {
      payload = buildPLPSPayload(section);
      const res = await triggePLPSPlanner(payload, abortControllerRef.current.signal);
      // console.log("Planner Api Response", res);
      generatedText = res?.response || "Unable to Generate Text";
      // }
      // await handleSaveLog(section, generatedText);

      // Use section_id as key
      const sectionId = section?.section_id;
      const prevRuns = history[sectionId] || [];
      const newRun = {
        key: Date.now(),
        payload,
        generatedText,
        timestamp: new Date().toLocaleString(),
        author: userEmail,
      };

      setHistory((prev) => ({
        ...prev,
        [sectionId]: [newRun, ...prevRuns],
      }));

      message.success(`Generated text for ${section?.section_name}`);

      //Do not insert if history exists
      if (prevRuns?.length === 0) {
        await insertInstructionText(instructionText)
        await insertTemplateIntoWordPLPS({ ...section, generatedText }, protocolId, euctNumber);
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.warn(`Generation aborted for section: ${section?.section_name}`);
        message.warning(`Generation canceled for ${section?.section_name}`);
      } else {
        console.error(err);
        message.error(`Failed to generate text`);
      }
    } finally {
      if (index !== null) {
        setRowLoading(null);
        setRowLoader(false);
      }
    }
  };

  const handleInsertIntoWord = async (section) => {
    const sectionId = section.section_id;
    const latestRun = history[sectionId]?.[0];

    if (!latestRun) {
      message.warning("No generated text to insert");
      return;
    }
    await insertTemplateIntoWordPLPS({ ...section, generatedText: latestRun.generatedText }, fileList?.[0]?.name);
  };

  const handleGenerateAll = async () => {
    if (!sections.length) {
      message.warning("No sections to generate.");
      return;
    }

    setGenerateAllLoading(true);
    setCompletedCount(0);
    setGenerateProgress(0);
    message.info("Generating all sections...");

    // Reset cancel flag before starting
    cancelGenerationRef.current = false;
    const totalSections = sections.length;

    try {
      for (let i = 0; i < totalSections; i++) {
        if (cancelGenerationRef.current) {
          message.warning("Generation stopped by user.");
          break;
        }

        await handleGenerateForSection(sections[i], i);

        if (cancelGenerationRef.current) {
          message.warning("Generation stopped by user.");
          break;
        }
        await new Promise((r) => setTimeout(r, 500));
        setCompletedCount((prev) => prev + 1);
        setGenerateProgress(Math.round(((i + 1) / totalSections) * 100));
      }

      if (!cancelGenerationRef.current) {
        message.success("All sections processed successfully!");
      }
    } catch (error) {
      console.error("Error while generating sections:", error);
      message.error("An error occurred during generation. Please try again.");
    } finally {
      setGenerateAllLoading(false);
    }
  };

  const handleBack = () => {
    const isFileListEmpty = fileList.length === 0;
    const modalTitle = isFileListEmpty ? "Are you sure?" : "Discard Changes?";
    const modalContent = isFileListEmpty
      ? "Do you want to go back?"
      : "Your data will be discarded if you go back. Are you sure?";

    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      okText: "Yes, Go Back",
      cancelText: "Stay",
      okType: "danger",
      centered: true,
      onOk() {
        abortControllerHandle();
        //Cancel current API request
        abortControllerRef.current?.abort();
        //Stop the loop
        cancelGenerationRef.current = true;
        onBack?.();
        setTemplates([]);
        setSections([]);
        setHistory({});
        setSelectedRunIndexes({});
        setTemplateId(null);
        setGenerateProgress(0);
        setCompletedCount(0);
      },
      onCancel() {
        console.log("Stay on page");
      },
    });
  };

  const columns = [
    {
      title: "Section",
      dataIndex: "section_name",
      key: "section_name",
      width: "80%",
    },
    {
      title: "Action",
      key: "action",
      width: "20%",
      render: (_, section, index) => {
        const hasHistory = history?.[section?.section_id]?.length > 0;
        const isGenerating = rowLoading === index;

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            {isGenerating ? (
              <Spin size="small" style={{ marginRight: 6, marginLeft: 6 }} />
            ) : hasHistory ? (
              <Button
                icon={
                  <Tooltip title="Insert Latest into Word">
                    <GrDocumentDownload />
                  </Tooltip>
                }
                size="small"
                style={{ marginRight: "5px", color: "#27a6a4" }}
                onClick={() => handleInsertIntoWord(section)}
                type="text"
              />
            ) : (
              <Button
                icon={
                  <Tooltip title="Generate Text">
                    <ThunderboltOutlined />
                  </Tooltip>
                }
                size="small"
                style={{ marginRight: "5px", color: "#426bba" }}
                onClick={() => handleGenerateForSection(section, index)}
                type="text"
                disabled={loading || rowLoader}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "12px" }}>
      <GlobalStyle />

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          marginBottom: "8px",
          gap: "2rem",
        }}
      >
        {/* Back */}
        <StyledButton onClick={handleBack} disabled={loading}>
          Back
        </StyledButton>
        {fileList?.[0]?.name && <FileNameTag fileName={fileList?.[0]?.name} />}
      </div>

      {generateAllLoading ? (
        <Affix offsetTop={2}>
          <div
            style={{
              background:
                "linear-gradient(150deg, rgba(235, 240, 255, 1) 25%, rgba(225, 235, 255, 1) 50%, rgba(215, 230, 255, 1) 100%)",
              padding: "8px 16px",
              color: "#426BBA",
              zIndex: 1000,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "#e3ecff",
              color: "#426bba !important",
              fontSize: "12px",
              border: "none",
              borderRadius: "1rem",
              boxShadow: "0 2px 6px rgba(66, 107, 186, 0.2)",
              padding: "4px 10px",
              margin: "auto",
              marginBottom: "15px",
            }}
          >
            <div style={{ marginTop: 0, color: "#426bba", fontSize: "11px", marginRight: 3 }}>
              {completedCount}/{sections?.length || 0} sections generated
            </div>
            <div style={{ width: "35%", marginRight: 3, fontWeight: 600 }}>
              <StyledProgress
                percent={generateProgress}
                status={generateProgress === 100 ? "success" : "active"}
                strokeColor="#426bba"
                style={{ color: "#426bba", fontSize: "12px" }}
              />
            </div>
            <div style={{ overflow: "hidden" }}>
              {fileList?.[0]?.name && <FileNameTag fileName={fileList?.[0]?.name} showIconOnly={true} />}
            </div>
          </div>
        </Affix>
      ) : null}

      {/* Template dropdown */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <StyledSelect
          placeholder="Select Template"
          value={templateId}
          onChange={handleTemplateSelect}
          loading={templateLoad}
          disabled={rowLoader || templateLoad}
        >
          {templates.map((tpl) => (
            <Option key={tpl.template_id} value={tpl.template_id}>
              {tpl.template_name}
            </Option>
          ))}
        </StyledSelect>
      </div>

      {templateId && (
        <>
          <div
            style={{
              display: "grid",
              marginBottom: 20,
              marginTop: 20,
            }}
          >
            <StyledButton
              style={{ height: "30px", marginBottom: 20 }}
              onClick={handleGenerateAll}
              disabled={loading || rowLoading || generateAllLoading}
            >
              Generate All
            </StyledButton>
          </div>

          <Spin spinning={loading}>
            <StyledTableWrapper>
              <Table
                dataSource={sections.map((s, i) => ({ key: i, ...s }))}
                columns={columns}
                pagination={false}
                size="small"
              />
            </StyledTableWrapper>
          </Spin>
        </>
      )}
    </div>
  );
};

export default PLPSTemplate;
